import { useCallback, useEffect, useRef, useState } from "react";
import { format, startOfMonth } from "date-fns";
import { getAttendance, getAttendanceSummary } from "../../../../services/StudentPortal/StudentAttendanceService";
import { getEvents } from "../../../../services/StudentPortal/StudentEventService";
import { unwrap, unwrapList } from "../../utils/studentPortalUtils";
import { selectHolidayEvents } from "../../utils/studentEventUtils";
import { handleApiError } from "../../../../utils/errorHandler";

// Confirmed against the real backend (2026-10-22): a day record's real
// fields are att_date and att_marked (a human label like "Present" /
// "Absent"), with attendance_label.label_name as a backup. The other
// candidates are kept as fallbacks only.
const normalizeStatus = (value) => (value ? String(value).trim().toLowerCase() : null);

const pickDate = (item) =>
  item?.att_date || item?.date || item?.day || item?.attendance_date || item?.on_date || item?.marked_date || null;

const pickStatus = (item) =>
  item?.att_marked ||
  item?.attendance_label?.label_name ||
  item?.status_label ||
  item?.attendance_status ||
  item?.type ||
  item?.day_status ||
  null;

// Normalizes whatever shape the backend returns - an array of day records,
// or a { "2024-05-01": "present" } map - into a flat [{ date, status }] list.
const normalizeDays = (raw) => {
  if (!raw) return [];

  if (Array.isArray(raw)) {
    return raw
      .map((item) => {
        if (!item || typeof item === "string") return null;
        const date = pickDate(item);
        if (!date) return null;
        return { date: String(date).slice(0, 10), status: normalizeStatus(pickStatus(item)) };
      })
      .filter(Boolean);
  }

  if (typeof raw === "object") {
    return Object.entries(raw)
      .map(([date, value]) => {
        if (!date) return null;
        const status = typeof value === "string" ? value : pickStatus(value);
        return { date: date.slice(0, 10), status: normalizeStatus(status) };
      })
      .filter(Boolean);
  }

  return [];
};

// Confirmed against the real backend (2026-10-26): holidays come from the
// same student/event endpoint the Dashboard's "Upcoming Events" uses, not
// from attendance-summary — split by the event's own is_holiday flag.
const toHolidayItem = (event) => ({
  date: event?.event_date || "",
  name: event?.title || "Holiday",
});

const extractDayList = (data) => {
  if (!data) return null;
  if (Array.isArray(data)) return data;
  if (typeof data === "object") {
    if (Array.isArray(data.days)) return data.days;
    if (Array.isArray(data.records)) return data.records;
    if (Array.isArray(data.attendance)) return data.attendance;
    if (Array.isArray(data.data)) return data.data;
    return data; // treat as a date -> status map
  }
  return null;
};

const useStudentAttendance = (studentId, programId, sessionId, initialMonth = new Date()) => {
  const [month, setMonth] = useState(startOfMonth(initialMonth));
  const [days, setDays] = useState([]);
  const [summary, setSummary] = useState(null);
  const [holidays, setHolidays] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  // Separate from `isLoading`: `isLoading` only covers the very first fetch
  // (so the page can show its one full-screen Loader); month navigation
  // afterwards only flips this flag so the calendar can show its own
  // in-place loading state without unmounting the rest of the page.
  const [isMonthLoading, setIsMonthLoading] = useState(false);
  const hasLoadedOnce = useRef(false);

  const fetchAttendance = useCallback(async (forMonth) => {
    if (!studentId || !programId || !sessionId) return;
    if (hasLoadedOnce.current) {
      setIsMonthLoading(true);
    }
    try {
      // Confirmed against the real backend (2026-10-22): student_id,
      // program_id and session_id are all required alongside month/year.
      const params = {
        student_id: studentId,
        program_id: programId,
        session_id: sessionId,
        month: format(forMonth, "MM"),
        year: format(forMonth, "yyyy"),
      };

      const [attendanceResponse, summaryResponse] = await Promise.all([
        getAttendance(params).catch((error) => {
          handleApiError(error);
          return null;
        }),
        getAttendanceSummary(params).catch((error) => {
          handleApiError(error);
          return null;
        }),
      ]);

      const attendanceData = attendanceResponse ? unwrap(attendanceResponse, null) : null;
      const normalizedDays = normalizeDays(extractDayList(attendanceData));
      const summaryData = summaryResponse ? unwrap(summaryResponse, {}) || {} : {};

      setDays(normalizedDays);
      setSummary(summaryData);
    } finally {
      if (hasLoadedOnce.current) {
        setIsMonthLoading(false);
      } else {
        setIsLoading(false);
        hasLoadedOnce.current = true;
      }
    }
  }, [studentId, programId, sessionId]);

  // The event calendar isn't month-scoped (the endpoint returns the whole
  // session at once, same as the Dashboard's call), so this only re-fetches
  // when the student/session identity changes — not on every month nav.
  useEffect(() => {
    if (!studentId || !programId || !sessionId) return;
    getEvents({ student_id: studentId, program_id: programId, session_id: sessionId })
      .then((response) => setHolidays(selectHolidayEvents(unwrapList(response)).map(toHolidayItem)))
      .catch(() => setHolidays([]));
  }, [studentId, programId, sessionId]);

  useEffect(() => {
    fetchAttendance(month);
  }, [month, fetchAttendance]);

  return { summary, days, holidays, isLoading, isMonthLoading, month, setMonth };
};

export default useStudentAttendance;
